/**
 * Clickable Text Component
 *
 * Renders text where individual words can be clicked to explore their meaning.
 * Used in exercises, chat messages, and other contexts where word exploration is helpful.
 */

import React, { useState } from 'react';
import WordExplorer from '../chat/WordExplorer';

interface ClickableTextProps {
  text: string;
  className?: string;
  /** If true, wrap in a paragraph tag. If false, use a span */
  asParagraph?: boolean;
}

const ClickableText: React.FC<ClickableTextProps> = ({
  text,
  className = '',
  asParagraph = false
}) => {
  const [selectedWord, setSelectedWord] = useState<{
    word: string;
    fullText: string;
  } | null>(null);

  const handleWordClick = (word: string) => {
    // Create a delimited version of the text with the clicked word
    const delimitedText = text.replace(
      new RegExp(`\\b${word}\\b`, 'i'),
      `|${word}|`
    );

    setSelectedWord({
      word,
      fullText: delimitedText,
    });
  };

  const handleCloseExplorer = () => {
    setSelectedWord(null);
  };

  // Split text into words while preserving punctuation
  const renderClickableWords = () => {
    // Match words and non-word characters separately
    const tokens = text.split(/(\s+|[.,;:!?()[\]{}"""''—-])/);

    return tokens.map((token, index) => {
      // If it's whitespace or punctuation, render as-is
      if (/^\s+$/.test(token) || /^[.,;:!?()[\]{}"""''—-]$/.test(token)) {
        return <span key={index}>{token}</span>;
      }

      // If it's a word, make it clickable
      if (token.trim()) {
        return (
          <span
            key={index}
            className="cursor-pointer hover:text-primary hover:underline transition-colors"
            onClick={() => handleWordClick(token.trim())}
          >
            {token}
          </span>
        );
      }

      return null;
    });
  };

  const content = (
    <>
      {renderClickableWords()}
      {selectedWord && (
        <div className="mt-4 p-4 bg-base-100 rounded-lg border-2 border-primary shadow-lg">
          <WordExplorer
            text={selectedWord.fullText}
            clickedWord={selectedWord.word}
            onClose={handleCloseExplorer}
          />
        </div>
      )}
    </>
  );

  if (asParagraph) {
    return <p className={className}>{content}</p>;
  }

  return <span className={className}>{content}</span>;
};

export default ClickableText;
